import { Fragment, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { OauthIntegrationProviderEnum } from 'src/graphql/schema';
import { useIntegrationAuth } from 'src/hooks';
import { getProviderDisplayName } from 'src/utils/getProviderDisplayName';
import { renderLogo } from 'src/utils/renderLogo';
import { Button } from '@/components/ui/button';

import { useIntegration } from '../model';

const Container = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { provider, tenantId, reconnectIntegrationId, resetIntegration } = useIntegration();
  const { reinitAuth } = useIntegrationAuth(provider as OauthIntegrationProviderEnum);

  if (!provider || !reconnectIntegrationId) return null;

  const Icon = renderLogo(provider);
  const name = getProviderDisplayName(provider);

  const onReconnect = async () => {
    setIsLoading(true);

    try {
      await reinitAuth(reconnectIntegrationId, tenantId ? { tenantId } : undefined);
      resetIntegration();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Fragment>
      <DialogHeader className="text-center gap-6">
        <Icon className="size-10 mx-auto" />
        <DialogTitle className="text-xl font-bold">{name}</DialogTitle>
        <DialogDescription className="font-medium">
          Authorize Codekeeper to securely connect with your {name} account using OAuth.
          <br />
          <br />
          This allows you to sync your list of repositories, select which ones to deposit, and keep the connection
          monitored automatically.
        </DialogDescription>
      </DialogHeader>
      <Button onClick={onReconnect} disabled={isLoading} isLoading={isLoading} size="sm">
        Reconnect to {name} <ExternalLink />
      </Button>
    </Fragment>
  );
};

const ReconnectOauthDialog = () => {
  const { resetIntegration, isReconnectOpen } = useIntegration();

  const onOpenChange = (open: boolean) => {
    if (!open) resetIntegration();
  };

  return (
    <Dialog open={isReconnectOpen} onOpenChange={onOpenChange}>
      <DialogContent className="md:max-w-[600px] gap-6">
        <Container />
      </DialogContent>
    </Dialog>
  );
};

export default ReconnectOauthDialog;
